import clsx from 'clsx'
import { Link } from 'react-router-dom'

export type SideNavSubItemProps = {
  /** The label text displayed in the nav item */
  label: string
  /** The route to navigate to */
  to: string
  /** Icon component to display (from @/assets/icons) */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  /** Whether the item is currently active/selected */
  isActive?: boolean
  /** Whether to show the beta tag */
  showBetaTag?: boolean
  /** Click handler for the nav item */
  onClick?: () => void
  /** Whether the item is disabled */
  disabled?: boolean
}

/**
 * A sub-item for use inside SideNavGroup.
 *
 * Same states as SideNavItem but designed to be wrapped by the group's border container.
 */
export function SideNavSubItem({ label, to, icon: Icon, isActive = false, showBetaTag = false, onClick, disabled = false }: SideNavSubItemProps) {
  return (
    <Link
      to={to}
      onClick={disabled ? (e) => e.preventDefault() : onClick}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={clsx(
        // Base styles
        'group flex w-full items-center gap-2 rounded-md p-2 text-sm font-semibold leading-5 h-10',
        // Focus styles (keyboard navigation - subitems have no focus ring and inset shadow)
        'focus:outline-none focus-visible:outline-none focus-visible:shadow-[0px_0px_0px_2px_rgba(42,73,255)_inset]',
        // State styles
        isActive ? 'bg-brand-neutral-100 text-brand-500' : 'text-brand-neutral-950 hover:bg-brand-neutral-100 active:bg-brand-neutral-200',
        // Disabled styles
        disabled && 'pointer-events-none opacity-50'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      {Icon && (
        <Icon
          className={clsx('h-5 w-5 shrink-0', isActive ? 'text-brand-500' : 'text-brand-neutral-600 group-hover:text-brand-neutral-700')}
          aria-hidden="true"
        />
      )}

      <span className={clsx('font-rethink-sans', isActive && 'text-brand-500')}>{label}</span>

      {showBetaTag && (
        <span className="ml-auto shrink-0 rounded bg-brand-neutral-950 px-1 py-px text-[10px] font-medium leading-[11px] text-white [font-variant:small-caps]">
          beta
        </span>
      )}
    </Link>
  )
}
